import type { TFunction } from "i18next";
import { getPermissionActionKey, getPermissionModuleKey, parsePermissionName } from "@/lib/permission-groups";

export type PermissionLabelSource = {
	name: string;
	type?: string;
	action?: string;
};

function humanizeKey(key: string): string {
	if (!key) return "";
	return key
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/[._:-]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^./, c => c.toUpperCase());
}

/**
 * Resolves a display label for a permission: prefers `management.permissions.*`,
 * then composed module + action, then a humanized `name`.
 */
export function getPermissionDisplayLabel(t: TFunction, permission: PermissionLabelSource): string {
	const dotted = permission.name.replace(/:/g, ".");
	const primaryKey = `management.permissions.${dotted}`;
	const primary = t(primaryKey);
	if (typeof primary === "string" && primary.length > 0 && primary !== primaryKey) {
		return primary;
	}

	const { module: rawModule, action: rawAction } = parsePermissionName(permission.name);
	const lowerKey = `management.permissions.${rawModule.toLowerCase()}.${rawAction}`;
	if (rawModule && rawAction) {
		const alt = t(lowerKey);
		if (typeof alt === "string" && alt.length > 0 && alt !== lowerKey) {
			return alt;
		}
	}

	const moduleKey = getPermissionModuleKey(permission);
	const actionKey = getPermissionActionKey(permission);
	const moduleName = t(`permissions.types.${moduleKey}`, {
		defaultValue: humanizeKey(moduleKey),
	});
	const actionName = actionKey
		? t(`permissions.actionLabels.${actionKey}`, { defaultValue: humanizeKey(actionKey) })
		: "";

	if (moduleName && actionName) {
		return t("permissions.fallback.composed", {
			module: moduleName,
			action: actionName,
		});
	}

	if (moduleName) return moduleName;
	return humanizeKey(permission.name);
}

/** Tailwind classes for action chips (subtle semantic cues). */
export function permissionActionBadgeClass(action: string): string {
	switch (action) {
		case "read":
			return "border-sky-200/80 bg-sky-50 text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-100";
		case "create":
			return "border-emerald-200/80 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-100";
		case "update":
			return "border-amber-200/80 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100";
		case "delete":
			return "border-red-200/80 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100";
		case "export":
			return "border-violet-200/80 bg-violet-50 text-violet-900 dark:border-violet-900/50 dark:bg-violet-950/40 dark:text-violet-100";
		case "approve":
			return "border-teal-200/80 bg-teal-50 text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-100";
		case "receive":
			return "border-indigo-200/80 bg-indigo-50 text-indigo-900 dark:border-indigo-900/50 dark:bg-indigo-950/40 dark:text-indigo-100";
		default:
			return "border-border/80 bg-muted/60 text-foreground";
	}
}
